
// Set to track which investors are currently being processed
export const processingInvestors = new Set<string>();

// Map to track which investors have failed processing
export const failedInvestors = new Map<string, Error>();

// Flag to track if processing is in a forced disabled state
let processingForcedDisabled = false;

/**
 * Reset failed investors tracking
 */
export const resetFailedInvestors = () => {
  failedInvestors.clear();
  console.log("Failed investors tracking reset");
};

/**
 * Function to recover from batch processing failure
 * Clears processing state and unlocks any investors that were being processed
 */
export const recoverFromBatchFailure = () => {
  console.log("Recovering from batch processing failure");
  
  // Clear all processing state
  processingInvestors.clear();
  failedInvestors.clear();
  
  // Clear any batch progress markers
  localStorage.removeItem("batch_progress");
  localStorage.removeItem("batch_completion");
  
  // Reset forced disabled state
  processingForcedDisabled = false;
  
  // Record recovery timestamp
  localStorage.setItem("last_recovery_timestamp", Date.now().toString());
  
  return true;
};

/**
 * Temporarily disable processing to prevent race conditions
 */
export const disableProcessing = () => {
  processingForcedDisabled = true;
  console.log("Processing temporarily disabled");
};

/**
 * Enable processing after it was disabled
 */
export const enableProcessing = () => {
  processingForcedDisabled = false;
  console.log("Processing enabled");
};

/**
 * Check if processing is currently disabled
 */
export const isProcessingDisabled = () => {
  return processingForcedDisabled;
};

/**
 * Function to check for idle timeout and handle session resume
 * This prevents data from being wiped after periods of inactivity
 */
export const checkSessionActivity = (): boolean => {
  const lastActivity = localStorage.getItem("last_activity_timestamp");
  const currentTime = Date.now();
  
  // Update the last activity timestamp
  localStorage.setItem("last_activity_timestamp", currentTime.toString());
  
  // If no previous activity or activity was recent, no need for recovery
  if (!lastActivity) {
    return false;
  }
  
  const inactivityTime = currentTime - parseInt(lastActivity);
  const inactiveThreshold = 10 * 60 * 1000; // 10 minutes
  
  // If application was inactive for more than the threshold, recovery may be needed
  if (inactivityTime > inactiveThreshold) {
    console.log(`Application was inactive for ${inactivityTime/1000} seconds, checking if recovery is needed`);
    return true;
  }
  
  return false;
};

/**
 * Function to prevent stale state when resuming after inactivity
 * Returns true if a reload is recommended, false otherwise
 */
export const checkForStaleState = (): boolean => {
  const lastStateSave = localStorage.getItem("last_state_save");
  
  // If we don't have a record of the last save, we can't determine if state is stale
  if (!lastStateSave) {
    return false;
  }
  
  const lastStateTime = parseInt(lastStateSave);
  const currentTime = Date.now();
  const staleThreshold = 15 * 60 * 1000; // 15 minutes
  
  // If state is older than threshold, it may be stale
  if (currentTime - lastStateTime > staleThreshold) {
    console.log(`State may be stale (saved ${(currentTime - lastStateTime)/1000} seconds ago)`);
    return true;
  }
  
  return false;
};

/**
 * Mark the current state as fresh
 */
export const markStateFresh = (): void => {
  localStorage.setItem("last_state_save", Date.now().toString());
};

/**
 * Check if a data sync operation is active
 */
export const checkSyncActivity = (): boolean => {
  const syncStartedAt = localStorage.getItem("sync_started_at");
  const syncCompletedAt = localStorage.getItem("sync_completed_at");
  
  if (syncStartedAt && !syncCompletedAt) {
    // There is an incomplete sync operation
    const syncStartTime = parseInt(syncStartedAt);
    const currentTime = Date.now();
    const timeSinceStart = currentTime - syncStartTime;
    
    // If sync has been running for more than 5 minutes, it might be stale
    if (timeSinceStart > 5 * 60 * 1000) {
      console.log(`Detected incomplete sync operation running for ${timeSinceStart/1000} seconds`);
      return true;
    }
  }
  
  return false;
};

/**
 * Mark start of a data sync operation
 */
export const markSyncStarted = (): void => {
  localStorage.setItem("sync_started_at", Date.now().toString());
  localStorage.removeItem("sync_completed_at");
};

/**
 * Mark completion of a data sync operation
 */
export const markSyncCompleted = (): void => {
  localStorage.setItem("sync_completed_at", Date.now().toString());
};
